import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Toolbar,
  Typography,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  People,
  AccessTime,
  EventNote,
  Logout,
  Login as LoginIcon,
  MonetizationOn,
  ChevronLeft,
  ExpandLess,
  ExpandMore,
  Settings,
  Business,
  SupportAgent,
  Notifications as NotificationsIcon,
  Person as ProfileIcon,
  School as TrainingIcon,
  Assessment as PerformanceIcon,
  Schedule as ScheduleIcon,
  Work as WorkIcon,
  MeetingRoom as MeetingRoomIcon,
  Restaurant as RestaurantIcon,
  Devices as DevicesIcon,
  CreditCard as CreditCardIcon,
  VpnKey,
  Build as SystemIcon,
  HealthAndSafety,
  Assignment as TasksIcon,
  AssignmentInd as UserManagementIcon,
  Timeline,
  Assessment,
  SyncAlt,
  Security as SecurityIcon,
  Apartment as FacilityIcon,
  LockPerson as AccessControlIcon,
  BarChart as ReportsIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { styled, useTheme as useMuiTheme } from '@mui/material/styles';
import MuiAppBar from '@mui/material/AppBar';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import axios from 'axios';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import { DarkModeIcon, LightModeIcon } from './ThemeIcons';
import Menu from '@mui/material/Menu';
import DeviceStatusIndicator from './DeviceStatusCard';
import LeaveApproval from '../pages/LeaveApproval';
import HealthSafety from '../pages/HealthSafety';
import Performance from '../pages/Performance';
import Projects from '../pages/Projects';

const drawerWidth = 240;

const AppBarStyled = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
  boxShadow: theme.shadows[4],
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

const FooterStyled = styled('footer')(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
  textAlign: 'center',
}));

function Layout() {
  const theme = useMuiTheme();
  const [open, setOpen] = useState(false);
  const [subOpen, setSubOpen] = useState({});
  const { logout, user, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);
  const drawerRef = useRef(null);
  const [supportOpen, setSupportOpen] = useState(false);
  const [supportEmail, setSupportEmail] = useState('');
  const [supportMessage, setSupportMessage] = useState('');
  const [supportTopic, setSupportTopic] = useState('Genel Destek');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [sendSuccess, setSendSuccess] = useState(false);
  const supportTopics = [
    'Genel Destek',
    'Kullanıcı Hesabı',
    'Çalışan Yönetimi',
    'Departman Yönetimi',
    'Maaş & Bordro',
    'Devamsızlık & İzin',
    'Teknik Sorun',
    'Öneri / Geri Bildirim',
  ];
  const { theme: customTheme, toggleTheme, themeMode, setTheme } = useCustomTheme();
  const [themeMenuAnchor, setThemeMenuAnchor] = useState(null);

  // Donanım cihazı IP'si (örnek: 79.76.44.140)
  const deviceIp = '79.76.44.140';
  const [deviceActive, setDeviceActive] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= theme.breakpoints.values.sm);
      if (window.innerWidth <= theme.breakpoints.values.sm) {
        setOpen(false); // Mobil görünümde çekmeceyi otomatik olarak kapat
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    const handleClickOutsideDrawer = (event) => {
      if (isMobile && open && drawerRef.current && !drawerRef.current.contains(event.target)) {
        handleDrawerClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutsideDrawer);

    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('mousedown', handleClickOutsideDrawer);
    };
  }, [theme.breakpoints.values.sm, isMobile, open]);

  useEffect(() => {
    // Eğer kullanıcı login ise ve e-posta varsa otomatik doldur
    if (typeof user === 'object' && user && user.email && !supportEmail) {
      setSupportEmail(user.email);
    }
  }, [user, supportEmail]);

  useEffect(() => {
    const checkDevice = async () => {
      try {
        // Backend'de /api/ping?ip=... gibi bir endpoint olmalı
        const res = await fetch(`/api/ping?ip=${deviceIp}`);
        const data = await res.json();
        setDeviceActive(data.active === true);
      } catch {
        setDeviceActive(false);
      }
    };
    checkDevice();
    const interval = setInterval(checkDevice, 10000); // Her 10 sn'de bir kontrol et
    return () => clearInterval(interval);
  }, [deviceIp]);

  const menuItems = [
    { 
      text: 'Genel Bakış', 
      icon: <Dashboard />, 
      children: [
        { text: 'Anasayfa', path: '/', icon: <Dashboard /> },
        { text: 'Profil', path: '/profile', icon: <ProfileIcon /> },
        { text: 'Bildirimler', path: '/notifications', icon: <NotificationsIcon /> },
      ]
    },
    { 
      text: 'Çalışan Yönetimi', 
      icon: <People />, 
      children: [
        { text: 'Çalışanlar', path: '/employees', icon: <People /> },
        { text: 'Departmanlar', path: '/departments', icon: <Business /> },
        { text: 'Eğitimler', path: '/training', icon: <TrainingIcon /> },
        { text: 'Performans Değerlendirme', path: '/performance', icon: <PerformanceIcon /> },
      ]
    },
    { 
      text: 'Zaman ve Devam Kontrolü', 
      icon: <AccessTime />, 
      children: [
        { text: 'Giriş-Çıkış', path: '/attendance', icon: <AccessTime /> },
        { text: 'Vardiyalar', path: '/shifts', icon: <ScheduleIcon /> },
        { text: 'Fazla Mesai', path: '/overtime', icon: <WorkIcon /> },
        { text: 'Zaman Çizelgesi', path: '/timesheet', icon: <AccessTime /> },
        { text: 'Giriş/Çıkış Kayıtları', path: '/entry-exit', icon: <AccessTime /> },
        { text: 'Ziyaretçiler', path: '/visitors', icon: <People /> },
      ]
    },
    { 
      text: 'İzin ve Devamsızlık', 
      icon: <EventNote />, 
      children: [
        { text: 'İzin Talepleri', path: '/leave-requests', icon: <EventNote /> },
        { text: 'İzin Onay', path: '/leave-approval', icon: <EventNote /> },
        { text: 'Resmi Tatiller', path: '/official-holidays', icon: <EventNote /> },
      ]
    },
    { 
      text: 'Proje Yönetimi', 
      icon: <WorkIcon />, 
      children: [
        { text: 'Projeler', path: '/projects', icon: <WorkIcon /> },
        { text: 'Görevler', path: '/tasks', icon: <TasksIcon /> },
      ]
    },
    { 
      text: 'Yemek Yönetimi', 
      icon: <RestaurantIcon />, 
      children: [
        { text: 'Kafeterya', path: '/cafeteria', icon: <RestaurantIcon /> },
        { text: 'Yemek Menüsü', path: '/meal-menu', icon: <RestaurantIcon /> },
      ]
    },
    { 
      text: 'Tesis Yönetimi', 
      icon: <MeetingRoomIcon />, 
      children: [
        { text: 'Toplantı Odaları', path: '/meeting-rooms', icon: <MeetingRoomIcon /> },
        { text: 'Cihaz Yönetimi', path: '/device-management', icon: <DevicesIcon /> },
        { text: 'Tesis Yönetimi', path: '/facility-management', icon: <FacilityIcon /> },
        { text: 'Erişim Kontrolü', path: '/facility-access-control', icon: <AccessControlIcon /> },
        { text: 'Kullanım Raporları', path: '/facility-usage-reports', icon: <ReportsIcon /> },
      ]
    },
    { 
      text: 'Güvenlik ve Kimlik', 
      icon: <SecurityIcon />, 
      children: [
        { text: 'RFID Kartlar', path: '/rfidcards', icon: <VpnKey /> },
      ]
    },
    { 
      text: 'Sistem Ayarları', 
      icon: <Settings />, 
      children: [
        { text: 'Kullanıcı Yönetimi', path: '/register', icon: <UserManagementIcon /> },
        { text: 'Şirket Ayarları', path: '/company-settings', icon: <Settings /> },
        { text: 'Entegrasyonlar', path: '/integrations', icon: <SyncAlt /> },
        { text: 'Denetim Kayıtları', path: '/audit-logs', icon: <Timeline /> },
        { text: 'Raporlar', path: '/reports', icon: <Assessment /> },
      ]
    },
    { 
      text: 'İş Sağlığı ve Güvenliği', 
      icon: <HealthAndSafety />, 
      children: [
        { text: 'İSG Takip', path: '/health-safety', icon: <HealthAndSafety /> },
      ]
    },
  ];

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
    setSubOpen({}); // Çekmece kapanınca alt menüleri de kapat
  };

  const handleSubMenuToggle = (index) => {
    setSubOpen(prevState => ({
      ...prevState,
      [index]: !prevState[index],
    }));
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      handleDrawerClose(); // Mobil görünümde navigasyon sonrası çekmeceyi kapat
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSupportSend = async () => {
    setSending(true);
    setSendError('');
    setSendSuccess(false);
    try {
      const res = await axios.post('https://pdks.bytebridge.com.tr/api/send-support-mail', {
        email: supportEmail,
        message: supportMessage,
        topic: supportTopic,
        subject: `PDKS Destek - ${supportTopic}`,
      });
      if (res.data.success) {
        setSendSuccess(true);
        setSupportOpen(false);
        setSupportEmail('');
        setSupportMessage('');
        setSupportTopic('Genel Destek');
      } else {
        setSendError('Mail gönderilemedi.');
      }
    } catch (err) {
      setSendError('Mail gönderilemedi: ' + (err.response?.data?.error || err.message));
    } finally {
      setSending(false);
    }
  };

  const handleThemeMenuOpen = (event) => {
    setThemeMenuAnchor(event.currentTarget);
  };
  const handleThemeMenuClose = () => {
    setThemeMenuAnchor(null);
  };
  const handleThemeChange = (mode) => {
    setTheme(mode);
    handleThemeMenuClose();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBarStyled position="fixed" open={open} color="primary">
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{ mr: 2, ...(open && { display: 'none' }) }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {user?.companyName || 'PDKS System'}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          {/* Tema ve Destek Butonları Yanyana, cihaz durumu sağda */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {!token && (
              <IconButton 
                color="inherit" 
                onClick={() => navigate('/login')}
                size="large"
                title="Giriş Yap"
              >
                <LoginIcon />
              </IconButton>
            )}
            <IconButton color="inherit" onClick={handleThemeMenuOpen} size="large">
              {themeMode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
            <DeviceStatusIndicator isActive={deviceActive} />
            <Menu
              anchorEl={themeMenuAnchor}
              open={Boolean(themeMenuAnchor)}
              onClose={handleThemeMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={() => handleThemeChange('light')}>Açık Tema</MenuItem>
              <MenuItem onClick={() => handleThemeChange('dark')}>Koyu Tema</MenuItem>
            </Menu>
            <IconButton
              color="inherit"
              aria-label="support"
              onClick={() => setSupportOpen(true)}
            >
              <SupportAgent/>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBarStyled>
      {/* Support Dialog */}
      <Dialog open={supportOpen} onClose={() => setSupportOpen(false)}>
        <DialogTitle>Destek Talebi</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="support-topic-label">Destek Konusu</InputLabel>
            <Select
              labelId="support-topic-label"
              value={supportTopic}
              label="Destek Konusu"
              onChange={e => setSupportTopic(e.target.value)}
            >
              {supportTopics.map(topic => (
                <MenuItem key={topic} value={topic}>{topic}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            autoFocus
            margin="dense"
            label="E-posta adresiniz"
            type="email"
            fullWidth
            value={supportEmail}
            onChange={e => setSupportEmail(e.target.value)}
            sx={{ mt: 2 }}
          />
          <TextField
            margin="dense"
            label="Mesajınız"
            type="text"
            fullWidth
            multiline
            minRows={4}
            value={supportMessage}
            onChange={e => setSupportMessage(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSupportOpen(false)} disabled={sending}>İptal</Button>
          <Button onClick={handleSupportSend} variant="contained" color="primary" disabled={!supportEmail || !supportMessage || sending}>
            {sending ? 'Gönderiliyor...' : 'Gönder'}
          </Button>
        </DialogActions>
        {sendError && <div style={{ color: 'red', marginTop: 8 }}>{sendError}</div>}
        {sendSuccess && <div style={{ color: 'green', marginTop: 8 }}>Mail başarıyla gönderildi!</div>}
      </Dialog>
      <Box sx={{ display: 'flex', flexGrow: 1 }}>
        <Drawer
          ref={drawerRef}
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              backgroundColor: theme.palette.mode === 'dark' ? '#18191a' : '#fff',
              color: theme.palette.mode === 'dark' ? '#fff' : '#18191a',
            },
          }}
          variant={isMobile ? 'temporary' : 'persistent'} // Mobil için geçici, masaüstü için kalıcı
          anchor="left"
          open={open}
          onClose={isMobile ? handleDrawerClose : undefined} // Mobil için onClose prop'u ekle
        >
          <DrawerHeader>
            <IconButton onClick={handleDrawerClose}>
              <ChevronLeft />
            </IconButton>
          </DrawerHeader>
          <Divider sx={{ backgroundColor: theme.palette.mode === 'dark' ? '#23272f' : '#e0e0e0' }} />
          <List>
            {menuItems.map((item, index) => (
              <React.Fragment key={item.text}>
                {item.children ? (
                  <>
                    <ListItem button onClick={() => handleSubMenuToggle(index)}>
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.text} />
                      {subOpen[index] ? <ExpandLess /> : <ExpandMore />}
                    </ListItem>
                    <Collapse in={subOpen[index]} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {item.children.map((child) => (
                          <ListItem
                            button
                            key={child.text}
                            onClick={() => handleNavigation(child.path)}
                            sx={{ 
                              pl: 4, 
                              backgroundColor: location.pathname === child.path ? theme.palette.action.selected : null 
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: '40px' }}>{child.icon}</ListItemIcon>
                            <ListItemText primary={child.text} />
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  </>
                ) : (
                  <ListItem
                    button
                    onClick={() => handleNavigation(item.path)}
                    sx={{ backgroundColor: location.pathname === item.path ? theme.palette.action.selected : null }}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItem>
                )}
              </React.Fragment>
            ))}
            <Divider sx={{ backgroundColor: theme.palette.mode === 'dark' ? '#23272f' : '#e0e0e0' }} />
            {token && (
              <ListItem button onClick={handleLogout}>
                <ListItemIcon>
                  <Logout />
                </ListItemIcon>
                <ListItemText primary="Çıkış Yap" />
              </ListItem>
            )}
            {!token && (
              <ListItem button onClick={() => navigate('/login')}>
                <ListItemIcon>
                  <LoginIcon />
                </ListItemIcon>
                <ListItemText primary="Giriş Yap" />
              </ListItem>
            )}
          </List>
        </Drawer>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            transition: theme.transitions.create('margin', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
            marginLeft: `-${drawerWidth}px`,
            ...(open && !isMobile && { // Sadece masaüstünde açık olduğunda margin uygula
              transition: theme.transitions.create('margin', {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.enteringScreen,
              }),
              marginLeft: 0,
            }),
            ...(open && isMobile && { // Mobilde açık olduğunda margin sıfırla (geçici çekmece)
              marginLeft: 0,
            }),
          }}
        >
          <DrawerHeader />
          <Outlet />
        </Box>
      </Box>
      <FooterStyled>
        <Typography variant="body2" color="text.secondary" align="center">
          {`Copyright © PDKS System By ByteBridge Software Solutions ${new Date().getFullYear()}`}
        </Typography>
      </FooterStyled>
    </Box>
  );
}

export default Layout;